import { action, atom, onCtxAbort, reaction } from '@reatom/framework'
import { match } from 'ts-pattern'

import { PoolEvent, PoolEventUpdateType } from '@blade/blockchain-types'
import { Array, pipe } from '@blade/std-lib'
import {
  feeConfigAtom,
  Network,
  selectedNetworkAtom,
} from '@root/entities/network'
import { pairEventsEvent } from '@root/entities/realtime'
import { ExploreFilter } from '@root/features/explore/model'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { isAppVisibleAtom } from '@root/shared/core'

import type {
  ExplorePoolsFilters,
  IPoolInfoExtended,
} from '@blade/blockchain-types'
import type { tradeApi } from '@blade/socket-types'
import type { Action } from '@reatom/framework'
import type { ExploreFiltersAtom } from '@root/features/explore'

type UpdateType = tradeApi.PairEventsData['updateType']
export type PairEventType = PoolEvent

export interface PairEventPayload {
  pairs: Array<IPoolInfoExtended>
  updateType: UpdateType
}

// eslint-disable-next-line @reatom/unit-naming-rule
export const newPairsEvent = action<PairEventPayload>('newPairEvent')
// eslint-disable-next-line @reatom/unit-naming-rule
export const cookingPairsEvent = action<PairEventPayload>('cookingPairEvent')
export const topGainerPairsEvent =
  // eslint-disable-next-line @reatom/unit-naming-rule
  action<PairEventPayload>('topGainerPairEvent')

export const trendingPairsEvent =
  // eslint-disable-next-line @reatom/unit-naming-rule
  action<PairEventPayload>('trendingPairEvent')

export const graduatedPairsEvent =
  // eslint-disable-next-line @reatom/unit-naming-rule
  action<PairEventPayload>('graduatedPairEvent')

export type PairsEvent =
  | typeof newPairsEvent
  | typeof topGainerPairsEvent
  | typeof trendingPairsEvent
  | typeof cookingPairsEvent
  | typeof graduatedPairsEvent

pairEventsEvent.onCall((ctx, data) => {
  const pairs = data.pools
  const network = ctx.get(selectedNetworkAtom)

  if (data.feeConfig) {
    feeConfigAtom(ctx, data.feeConfig)
  }

  // extra safety check
  if (!pairs[0] || Network.fromDto(pairs[0].network) !== network) {
    return
  }

  match(data.event)
    .with(PoolEvent.New, () => {
      newPairsEvent(ctx, { pairs: data.pools, updateType: data.updateType })
    })
    .with(PoolEvent.TopGainer, () => {
      topGainerPairsEvent(ctx, {
        pairs: data.pools,
        updateType: data.updateType,
      })
    })
    .with(PoolEvent.Trending, () => {
      trendingPairsEvent(ctx, {
        pairs: data.pools,
        updateType: data.updateType,
      })
    })
    .with(PoolEvent.Cooking, () => {
      cookingPairsEvent(ctx, {
        pairs: data.pools,
        updateType: data.updateType,
      })
    })
    .with(PoolEvent.Graduated, () => {
      graduatedPairsEvent(ctx, {
        pairs: data.pools,
        updateType: data.updateType,
      })
    })
    .exhaustive()
})

const subscribeReaction = reaction(
  (
    ctx,
    {
      event,
      filtersAtom,
    }: {
      event: PoolEvent
      filtersAtom?: ExploreFiltersAtom | undefined
    },
  ) => {
    const { mainWebsocket } = ctx.get(dependenciesAtom)
    const network = ctx.spy(selectedNetworkAtom)
    const filters = filtersAtom ? ctx.spy(filtersAtom) : undefined
    const filtersDto = filters
      ? (ExploreFilter.toPoolFiltersDto(filters) as ExplorePoolsFilters)
      : undefined

    mainWebsocket.pairEventsSubscribeAction(ctx, {
      network: Network.toSupportedNetwork(network),
      events: [event],
      filters: filtersDto,
    })

    onCtxAbort(ctx, () => {
      mainWebsocket.pairEventsUnsubscribeAction(ctx, {
        network: Network.toSupportedNetwork(network),
        events: [event],
        filters: filtersDto,
      })
    })
  },
  'subscribeReaction',
)

export const subscribeOnPairEventsAction = action(
  (
    ctx,
    {
      event,
      handler,
      maxCount,
      filtersAtom,
    }: {
      event: PoolEvent
      handler?: Action<[data: PairEventPayload]>
      maxCount: number
      filtersAtom?: ExploreFiltersAtom
    },
  ) => {
    const inactiveBufferAtom = atom<PairEventPayload | undefined>(
      undefined,
      'inactiveBufferAtom',
    )
    const sub = subscribeReaction(ctx, { event, filtersAtom })
    const eventAction = match(event)
      .with(PoolEvent.New, () => newPairsEvent)
      .with(PoolEvent.TopGainer, () => topGainerPairsEvent)
      .with(PoolEvent.Trending, () => trendingPairsEvent)
      .with(PoolEvent.Cooking, () => cookingPairsEvent)
      .with(PoolEvent.Graduated, () => graduatedPairsEvent)
      .exhaustive()

    const unsubscribeHandler = eventAction.onCall((ctx, data) => {
      const isVisible = ctx.get(isAppVisibleAtom)
      if (!isVisible) {
        if (data.updateType === PoolEventUpdateType.Append) {
          const currentPairs = ctx.get(inactiveBufferAtom)?.pairs ?? []

          const updatedPairs = pipe(
            data.pairs,
            Array.concat(currentPairs),
            Array.uniqBy((it) => it.poolAddress),
            Array.take(maxCount),
          )

          inactiveBufferAtom(ctx, {
            pairs: updatedPairs,
            updateType: data.updateType,
          })
        }

        if (data.updateType === PoolEventUpdateType.Replace) {
          inactiveBufferAtom(ctx, data)
        }
        return
      }
      handler?.(ctx, data)
    })

    const appVisibleReaction = reaction((ctx) => {
      const isVisible = ctx.spy(isAppVisibleAtom)
      if (!isVisible) {
        return
      }
      const inactiveBuffer = ctx.get(inactiveBufferAtom)
      if (!inactiveBuffer) {
        return
      }
      handler?.(ctx, inactiveBuffer)
      inactiveBufferAtom(ctx, undefined)
    }, 'appVisibleReaction')

    const appVisibleSub = appVisibleReaction(ctx)

    return () => {
      appVisibleSub.unsubscribe()
      sub.unsubscribe()
      unsubscribeHandler()
    }
  },
  'subscribeOnPairEventsAction',
)
